import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

// Layouts
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';

// Pages
import Login from './pages/auth/Login';
import MicrosoftCallback from './pages/auth/MicrosoftCallback';
import AcceptInvitation from './pages/auth/AcceptInvitation';
import ForceChangePassword from './pages/auth/ForceChangePassword';
import Dashboard from './pages/Dashboard';
import Projects from './pages/projects/Projects';
import ProjectPage from './pages/projects/ProjectPage';
import TaskDetail from './pages/tasks/TaskDetail';
import Users from './pages/users/Users';
import Workflows from './pages/workflows/Workflows';
import AuditLogs from './pages/audit/AuditLogs';
import TaskTypeManager from './pages/admin/TaskTypeManager';
import Profile from './pages/profile/Profile';
import DocumentViewer from './pages/DocumentViewer';
import LibraryPage from './pages/library/LibraryPage';
import ReportingPage from './pages/reporting/ReportingPage';
import NotFound from './pages/NotFound';

// Services
import sessionManager from './services/sessionManager';
import { fetchSessionConfig, clearAuthAndRedirect } from './services/api';
import { forceLogout, fetchCurrentUser } from './store/slices/authSlice';

// Context
import { ThemeProvider } from './contexts/ThemeContext';
import { UploadManagerProvider } from './contexts/UploadManagerContext';

// Components
import UploadProgressWidget from './components/common/UploadProgressWidget';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const location = useLocation();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  if (!isAuthenticated) {
    const from = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/login" replace state={{ from }} />;
  }

  // Force local users to change their password on first login
  if (user?.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirects if already logged in)
const PublicRoute = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated } = useSelector((state) => state.auth);

  if (isAuthenticated) {
    const storedRedirect = sessionStorage.getItem('postLoginRedirect');
    if (storedRedirect) {
      sessionStorage.removeItem('postLoginRedirect');
    }
    const from = location.state?.from || storedRedirect;
    return <Navigate to={from || '/dashboard'} replace />;
  }

  return children;
};

// Session Warning Modal Component
const SessionWarningModal = ({ show, remainingMinutes, onExtend, onLogout }) => {
  if (!show) return null;

  return (
    <div className="session-warning-overlay">
      <div className="session-warning-modal">
        <div className="session-warning-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#FF8C00" strokeWidth="2" />
            <path d="M12 8V12M12 16H12.01" stroke="#FF8C00" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <h3>Session Expiring Soon</h3>
        <p>Your session will expire in {remainingMinutes} minute{remainingMinutes > 1 ? 's' : ''} due to inactivity.</p>
        <div className="session-warning-buttons">
          <button className="btn btn-primary" onClick={onExtend}>
            Stay Logged In
          </button>
          <button className="btn btn-secondary" style={{ marginTop: '0px' }} onClick={onLogout}>
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMinutes, setWarningMinutes] = useState(2);

  // Handle session warning
  const handleSessionWarning = useCallback(({ remainingMinutes }) => {
    setWarningMinutes(remainingMinutes);
    setShowWarning(true);
  }, []);

  // Handle session expiry
  const handleSessionExpired = useCallback((message) => {
    setShowWarning(false);
    dispatch(forceLogout(message));
    clearAuthAndRedirect(message);
  }, [dispatch]);

  // Extend session
  const handleExtendSession = useCallback(() => {
    sessionManager.extendSession();
    setShowWarning(false);
  }, []);

  // Logout from warning
  const handleLogoutFromWarning = useCallback(() => {
    setShowWarning(false);
    clearAuthAndRedirect('You have been logged out.');
  }, []);

  // Refresh user data from server on every app load to pick up role changes
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCurrentUser());
    }
  }, []); // eslint-disable-line

  // Initialize session manager when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Fetch session config and initialize session manager
      fetchSessionConfig().then((config) => {
        sessionManager.init({
          idleTimeoutMinutes: config.session_idle_timeout_minutes,
          onWarning: handleSessionWarning,
          onSessionExpired: handleSessionExpired,
        });
      }).catch(() => {
        // Use defaults if fetch fails
        sessionManager.init({
          idleTimeoutMinutes: 30,
          onWarning: handleSessionWarning,
          onSessionExpired: handleSessionExpired,
        });
      });
    } else {
      // Destroy session manager when not authenticated
      sessionManager.destroy();
      setShowWarning(false);
    }

    return () => {
      // Cleanup on unmount
    };
  }, [isAuthenticated, handleSessionWarning, handleSessionExpired]);

  return (
    <ThemeProvider>
      <UploadManagerProvider>
        <SessionWarningModal
          show={showWarning}
          remainingMinutes={warningMinutes}
          onExtend={handleExtendSession}
          onLogout={handleLogoutFromWarning}
        />
        <UploadProgressWidget />
        <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <AuthLayout>
                <Login />
              </AuthLayout>
            </PublicRoute>
          }
        />

        <Route
          path="/accept-invite"
          element={
            <AuthLayout>
              <AcceptInvitation />
            </AuthLayout>
          }
        />

        {/* Force password change for local users on first login */}
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <AuthLayout>
                <ForceChangePassword />
              </AuthLayout>
            </ProtectedRoute>
          }
        />

        {/* Microsoft OAuth2 callback — must be a bare page (no AuthLayout/PublicRoute
             since the user is mid-authentication when they arrive here) */}
        <Route path="/auth/callback" element={<MicrosoftCallback />} />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />

          {/* Profile */}
          <Route path="profile" element={<Profile />} />

          {/* Projects */}
          <Route path="projects" element={<Projects />} />
          <Route path="projects/:projectId" element={<ProjectPage />} />
          <Route path="projects/:projectId/board" element={<ProjectPage />} />
          <Route path="projects/:projectId/settings" element={<ProjectPage />} />
          <Route path="projects/:projectId/knowledge" element={<ProjectPage />} />
          <Route path="projects/:projectId/allwork" element={<ProjectPage />} />
          <Route path="projects/:projectId/allissues" element={<ProjectPage />} />
          <Route path="projects/:projectId/executive-plan" element={<ProjectPage />} />

          {/* Tasks/Tickets - Support both old ID-based and new key-based routing */}
          <Route path="projects/:projectKey/tickets/:ticketKey" element={<TaskDetail />} />
          <Route path="projects/:projectId/board/:taskKey" element={<TaskDetail />} />
          <Route path="projects/:projectId/board/:parentKey/:taskKey" element={<TaskDetail />} />
          <Route path="tasks/:taskId" element={<TaskDetail />} />

          {/* Library */}
          <Route path="library" element={<LibraryPage />} />

          {/* Reporting */}
          <Route path="reporting" element={<ReportingPage />} />
          <Route path="reporting/d/:docId" element={<ReportingPage />} />

          {/* Document Viewer */}
          <Route path="viewer/:attachmentId" element={<DocumentViewer />} />

          {/* Users - Admin only */}
          <Route
            path="users"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'manager']}>
                <Users />
              </ProtectedRoute>
            }
          />

          {/* Workflows - Admin only */}
          <Route
            path="workflows"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                <Workflows />
              </ProtectedRoute>
            }
          />

          {/* Audit Logs - Admin only */}
          <Route
            path="audit"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                <AuditLogs />
              </ProtectedRoute>
            }
          />

          {/* Task Types - Admin only */}
          <Route
            path="task-types"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                <TaskTypeManager />
              </ProtectedRoute>
            }
          />

        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </UploadManagerProvider>
    </ThemeProvider>
  );
}

export default App;
